import { Home, Mail, MapPin, Pencil, Phone, Trash2, UserRound } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button } from "../components/Button";
import { ActionRow, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Field, SectionHeader } from "../components/UI";
import {
  createAddress,
  deleteAddress,
  getAddresses,
  getProfile,
  getUserId,
  type AddressData,
  type AddressRecord,
  updateAddress,
  updateProfile,
  type User
} from "../lib/api";
import { useAppState } from "../state/AppState";

type ProfileFormState = {
  full_name: string;
  email: string;
  phone: string;
};

type AddressFormState = AddressData & {
  type: string;
};

function buildProfileForm(user?: User | null): ProfileFormState {
  return {
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || ""
  };
}

function buildAddressForm(address?: AddressRecord | null): AddressFormState {
  return {
    name: address?.address_data?.name || "",
    phone: address?.address_data?.phone || "",
    line1: address?.address_data?.line1 || "",
    line2: address?.address_data?.line2 || "",
    city: address?.address_data?.city || "",
    state: address?.address_data?.state || "",
    pincode: address?.address_data?.pincode || "",
    type: address?.type || "shipping"
  };
}

export function ProfilePage() {
  const { addToast, setUser, user } = useAppState();
  const userId = getUserId(user);
  const sessionToken = user?.token || "";
  const [profileForm, setProfileForm] = useState<ProfileFormState>(() => buildProfileForm(user));
  const [addresses, setAddresses] = useState<AddressRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<number | null>(null);
  const [addressForm, setAddressForm] = useState<AddressFormState>(() => buildAddressForm());

  useEffect(() => {
    if (!user || !userId || !user.token) return;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getProfile(userId, user.token).catch(() => user),
      getAddresses(userId, user.token).catch(() => [])
    ])
      .then(([profile, addressRows]) => {
        if (cancelled) return;
        setProfileForm(buildProfileForm(profile));
        setAddresses(addressRows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, userId]);

  const sortedAddresses = useMemo(
    () => [...addresses].sort((a, b) => a.id - b.id),
    [addresses]
  );
  const displayName = profileForm.full_name || user?.full_name || user?.email || "ProtoDeccan user";
  const profileInitials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "PD";

  if (!user) {
    return (
      <EmptyState icon={<UserRound size={24} />} title="Login To Edit Profile">
        Profile and saved addresses will appear after login.
      </EmptyState>
    );
  }

  async function handleProfileSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!userId || !sessionToken) return;
    setSavingProfile(true);
    try {
      const nextUser = await updateProfile(userId, sessionToken, profileForm);
      setUser({
        ...user,
        full_name: nextUser.full_name,
        email: nextUser.email,
        phone: nextUser.phone
      });
      addToast("Profile updated", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to update profile.", "error");
    } finally {
      setSavingProfile(false);
    }
  }

  function openNewAddressForm() {
    setEditingAddressId(null);
    setAddressForm(buildAddressForm());
    setShowAddressForm(true);
  }

  function openEditAddressForm(address: AddressRecord) {
    setEditingAddressId(address.id);
    setAddressForm(buildAddressForm(address));
    setShowAddressForm(true);
  }

  async function handleAddressSubmit() {
    if (!userId || !sessionToken) return;
    setSavingAddress(true);
    try {
      const payload: AddressData = {
        name: addressForm.name || "",
        phone: addressForm.phone || "",
        line1: addressForm.line1 || "",
        line2: addressForm.line2 || "",
        city: addressForm.city || "",
        state: addressForm.state || "",
        pincode: addressForm.pincode || ""
      };

      if (editingAddressId) {
        const updated = await updateAddress(userId, sessionToken, editingAddressId, payload, addressForm.type || "shipping");
        setAddresses((current) => current.map((item) => (item.id === updated.id ? updated : item)));
        addToast("Address updated", "success");
      } else {
        const created = await createAddress(userId, sessionToken, payload, (addressForm.type || "shipping") as "shipping" | "billing" | "both");
        setAddresses((current) => [...current, created]);
        addToast("Address saved", "success");
      }
      setShowAddressForm(false);
      setEditingAddressId(null);
      setAddressForm(buildAddressForm());
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to save address.", "error");
    } finally {
      setSavingAddress(false);
    }
  }

  async function handleDeleteAddress(addressId: number) {
    if (!userId || !sessionToken) return;
    try {
      await deleteAddress(userId, sessionToken, addressId);
      setAddresses((current) => current.filter((item) => item.id !== addressId));
      if (editingAddressId === addressId) {
        setShowAddressForm(false);
        setEditingAddressId(null);
        setAddressForm(buildAddressForm());
      }
      addToast("Address deleted", "success");
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Failed to delete address.", "error");
    }
  }

  return (
    <section>
      <SectionHeader
        title="Profile"
        description="Manage your account details and saved delivery addresses."
        className="profile-header"
      />

      <Card className="profile-overview-card">
        <div className="profile-avatar" aria-hidden="true">{profileInitials}</div>
        <div className="profile-overview-copy">
          <span>Signed in as</span>
          <strong>{displayName}</strong>
          <div className="profile-overview-meta">
            {profileForm.email ? (
              <span><Mail size={14} aria-hidden="true" />{profileForm.email}</span>
            ) : null}
            {profileForm.phone ? (
              <span><Phone size={14} aria-hidden="true" />{profileForm.phone}</span>
            ) : null}
            <span><Home size={14} aria-hidden="true" />{sortedAddresses.length} saved {sortedAddresses.length === 1 ? "address" : "addresses"}</span>
          </div>
        </div>
      </Card>

      <div className="profile-layout">
        <Card className="profile-panel">
          <form onSubmit={handleProfileSubmit}>
            <CardHeader>
              <CardTitle>Personal Info</CardTitle>
              <CardDescription>Keep contact details updated for quotes, orders, and delivery calls.</CardDescription>
            </CardHeader>
            <CardContent>
              <Field label="Full Name">
                <input
                  className="form-control"
                  value={profileForm.full_name}
                  onChange={(event) => setProfileForm((current) => ({ ...current, full_name: event.currentTarget.value }))}
                />
              </Field>
              <Field label="Email">
                <input
                  className="form-control"
                  value={profileForm.email}
                  onChange={(event) => setProfileForm((current) => ({ ...current, email: event.currentTarget.value }))}
                />
              </Field>
              <Field label="Phone">
                <input
                  className="form-control"
                  value={profileForm.phone}
                  onChange={(event) => setProfileForm((current) => ({ ...current, phone: event.currentTarget.value }))}
                />
              </Field>
            </CardContent>
            <Button className="btn-block" type="submit" loading={savingProfile}>
              {savingProfile ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </Card>

        <Card className="profile-panel" id="addresses-card">
          <CardHeader className="profile-panel-header">
            <div>
              <CardTitle>Saved Addresses</CardTitle>
              <CardDescription>Used during checkout for shipping and billing.</CardDescription>
            </div>
            <Button variant="secondary" size="sm" onClick={openNewAddressForm}>
              Add Another Address
            </Button>
          </CardHeader>

          <CardContent id="addresses-list">
            {loading ? <div className="text-muted text-sm">Loading...</div> : null}
            {!loading && !sortedAddresses.length ? <div className="text-muted text-sm">No saved addresses yet.</div> : null}
            {sortedAddresses.map((address) => (
              <article className="profile-address-card" key={address.id}>
                <div className="profile-address-icon" aria-hidden="true">
                  <MapPin size={18} />
                </div>
                <div className="profile-address-actions">
                  <button type="button" aria-label="Edit address" onClick={() => openEditAddressForm(address)}>
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  <button type="button" aria-label="Delete address" onClick={() => handleDeleteAddress(address.id)}>
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
                <div className="profile-address-body">
                  <span className="badge profile-address-type">{address.type || "shipping"}</span>
                  <div><strong>{address.address_data.name || "Saved address"}</strong></div>
                  {address.address_data.phone ? <div className="profile-address-muted">{address.address_data.phone}</div> : null}
                  <div>{address.address_data.line1}</div>
                  {address.address_data.line2 ? <div>{address.address_data.line2}</div> : null}
                  <div>
                    {[address.address_data.city, address.address_data.state, address.address_data.pincode]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                </div>
              </article>
            ))}
          </CardContent>

          <div id="address-form" className={showAddressForm ? "address-edit-panel" : "hidden address-edit-panel"}>
            <Field label="Address Type">
              <select
                className="form-control"
                value={addressForm.type || "shipping"}
                onChange={(event) => setAddressForm((current) => ({ ...current, type: event.currentTarget.value }))}
              >
                <option value="shipping">Shipping</option>
                <option value="billing">Billing</option>
                <option value="both">Both</option>
              </select>
            </Field>
            <Field label="Name">
              <input className="form-control" value={addressForm.name || ""} onChange={(event) => setAddressForm((current) => ({ ...current, name: event.currentTarget.value }))} />
            </Field>
            <Field label="Phone">
              <input className="form-control" value={addressForm.phone || ""} onChange={(event) => setAddressForm((current) => ({ ...current, phone: event.currentTarget.value }))} />
            </Field>
            <Field label="Line 1">
              <input className="form-control" value={addressForm.line1 || ""} onChange={(event) => setAddressForm((current) => ({ ...current, line1: event.currentTarget.value }))} />
            </Field>
            <Field label="Line 2">
              <input className="form-control" value={addressForm.line2 || ""} onChange={(event) => setAddressForm((current) => ({ ...current, line2: event.currentTarget.value }))} />
            </Field>
            <Field label="City">
              <input className="form-control" value={addressForm.city || ""} onChange={(event) => setAddressForm((current) => ({ ...current, city: event.currentTarget.value }))} />
            </Field>
            <Field label="State">
              <input className="form-control" value={addressForm.state || ""} onChange={(event) => setAddressForm((current) => ({ ...current, state: event.currentTarget.value }))} />
            </Field>
            <Field label="Pincode">
              <input className="form-control" value={addressForm.pincode || ""} onChange={(event) => setAddressForm((current) => ({ ...current, pincode: event.currentTarget.value }))} />
            </Field>
            <ActionRow style={{ marginTop: "var(--space-md)" }}>
              <Button onClick={handleAddressSubmit} loading={savingAddress}>
                {savingAddress ? "Saving..." : "Save Address"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowAddressForm(false);
                  setEditingAddressId(null);
                  setAddressForm(buildAddressForm());
                }}
              >
                Cancel
              </Button>
            </ActionRow>
          </div>
        </Card>
      </div>
    </section>
  );
}
